import React, { useContext, useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Col,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Upload,
    message,
    Divider,
    Space,
    Radio,
    Typography
} from "antd";
import Editor from "@monaco-editor/react";
import { UploadOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import JutgeColors from "../../../../styles/JutgeColors";
import { PutJutgeProblemaIdTest } from "../../../../dao/jutge/PutJutgeProblemaIdTest";
import { PostJutgeProblemaIdTest } from "../../../../dao/jutge/PostJutgeProblemaIdTest";

const { TextArea } = Input;

interface Props {
    visible: boolean;
    onClose: (value: boolean) => void;
    id_activitat: string;
    id_problema: string;
    testData: any; // data to edit (or null for new)
    allTests?: any[]; // list of all tests for referencing
    onSave?: () => void;
    isMulti?: boolean;
}

const TestFormModal: React.FC<Props> = (props) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [methodList, setMethodList] = useState<string[]>([]); // Derived from 'class' tests if needed
    const [existingClassTests, setExistingClassTests] = useState<any[]>([]);
    const [existingMethodTests, setExistingMethodTests] = useState<any[]>([]);
    const [senseEntrada, setSenseEntrada] = useState(false);
    const { isLoggedIn } = useContext(AuthContext);
    const [mode, setMode] = useState(props.isMulti ? "class" : "es");
    const [enviado, setEnviado] = useState(false);
    const [category, setCategory] = useState<"single" | "multi">(props.isMulti ? "multi" : "single");

    useEffect(() => {
        if (!props.testData) {
            if (props.isMulti) {
                setCategory("multi");
                setMode("class");
            } else {
                setCategory("single");
                setMode("es");
            }
        }
    }, [props.isMulti, props.testData]);

    // Filter class tests for "Contenido en Clase" and "Detección Excepciones"
    useEffect(() => {
        if (props.allTests) {
            const classTests = props.allTests.filter(t => t.tipus === 'class');
            setExistingClassTests(classTests);

            // Collect 'method' tests (Check Mètode) defined in the same exercise.
            // These are used to populate the method dropdown for single-file "Check Mètode + E/S" tests.
            const methodTests = props.allTests.filter(t => t.tipus === 'method');
            setExistingMethodTests(methodTests);

            // Also collect methods from class tests if possible?
            // The class tests store JSON with methods list.
            // But we don't have easy access to parsed JSON here for all tests unless we parse them.
            // For now, "Detección Excepciones" requires "check class" test reference?
            // "allow linking to a specific method within a 'check class' test"
            // So user selects Class Test -> Then Selects Method from that class test.
        }
    }, [props.allTests]);

    useEffect(() => {
        if (props.visible) {
            if (props.testData) {
                loadTestData(props.testData);
            } else {
                form.resetFields();
                setMode(props.isMulti ? "class" : "es");
                setSenseEntrada(false);
            }
        }
    }, [props.visible, props.testData]);

    const mapTypes = ["Map", "HashMap", "LinkedHashMap", "TreeMap"];

    const processGenericsData = (items: any[]) => {
        if (!items) return items;
        return items.map((item: any) => {
            let newItem = { ...item };
            if (newItem.genericType && newItem.genericType.includes(",")) {
                const parts = newItem.genericType.split(",");
                newItem.genericTypeKey = parts[0].trim();
                newItem.genericTypeValue = parts[1].trim();
            }
            return newItem;
        });
    };

    const processGenericsWithParams = (items: any[]) => {
        if (!items) return items;
        return items.map((item: any) => {
            let newItem = { ...item };
            if (newItem.genericType && newItem.genericType.includes(",")) {
                const parts = newItem.genericType.split(",");
                newItem.genericTypeKey = parts[0].trim();
                newItem.genericTypeValue = parts[1].trim();
            }
            if (newItem.parameters) {
                newItem.parameters = processGenericsData(newItem.parameters);
            }
            return newItem;
        });
    };

    // Decodifica el contingut d'una fila 'entrada'/'sortida' agrupada.
    const decodeIoRow = (row: any): string => {
        if (!row || !row.fitxer) return "";
        try {
            const parts = String(row.fitxer).split(":");
            if (parts.length > 2) return atob(parts[2]);
            return atob(row.fitxer);
        } catch (e) {
            return String(row.fitxer);
        }
    };

    const loadTestData = (data: any) => {
        const initialValues: any = { ...data };

        // Fila agrupada E/S o Mètode + E/S (ve de TestsListModal amb entradaRow/sortidaRow).
        if (data.__pair) {
            setCategory("single");
            const isMethod = data.tipus === "methodtest";
            setMode(isMethod ? "methodtest" : "es");
            initialValues.entrada = decodeIoRow(data.entradaRow);
            initialValues.sortida = decodeIoRow(data.sortidaRow);
            if (isMethod) {
                initialValues.methodName = data.methodname || data.entradaRow?.methodname || "";
            }
            setSenseEntrada(!initialValues.entrada);
            form.setFieldsValue(initialValues);
            return;
        }

        try {
            const parts = data.fitxer ? data.fitxer.split(":") : [];
            const decoded = (parts.length > 2) ? atob(parts[2]) : (data.fitxer ? atob(data.fitxer) : "");

            // Different handling based on type
            if (["es", "content", "method", "methodtest"].includes(data.tipus)) {
                setCategory("single");
            } else {
                setCategory("multi");
            }

            if (data.tipus === "es" || data.tipus === "entrada" || data.tipus === "sortida") {
                setMode("es");
                // Logic for simple ES (legacy or current)
                // Assuming standard format or parts...
                // Actually existing logic in EditTestModal used specific logic.
                // Let's stick to what EditTestModal did.
                if (data.tipus === "entrada") {
                    initialValues.entrada = data.fitxer ? atob(data.fitxer.split(":")[2]) : "";
                    initialValues.sortida = "";
                    setSenseEntrada(false);
                } else if (data.tipus === "sortida") {
                    initialValues.entrada = "";
                    initialValues.sortida = data.fitxer ? atob(data.fitxer.split(":")[2]) : "";
                } else { // "es"
                    // Assuming 'fitxer' holds "input:base64" or something?
                    // EditTestModal logic:
                    // uses 'decoded' which was atob(parts[2]).
                    // If type is 'es', check how it was stored.
                    // JutgeService stores "raw" or splitted?
                    // EditTestModal assumed: fitxer = "ordre-tipus:content" ? No, atob(parts[2]) means "Prefix:Something:Base64".

                    // Let's try to parse flexibly.
                    if (parts.length >= 3) {
                        const content = atob(parts[2]);
                        initialValues.entrada = content;
                        initialValues.sortida = data.sortida || "";
                    } else {
                        // Fallback
                        initialValues.entrada = "";
                        initialValues.sortida = "";
                    }
                }
            } else if (data.tipus === "class") {
                setMode("class");
                try {
                    const classData = JSON.parse(decoded);
                    initialValues.className = classData.className;
                    initialValues.classType = classData.classType;
                    initialValues.classModifiers = classData.modifiers;
                    initialValues.extendedClass = classData.extendedClass;
                    initialValues.implementedInterfaces = classData.implementedInterfaces;
                    initialValues.constructors = processGenericsWithParams(classData.constructors);
                    initialValues.fields = processGenericsData(classData.fields);
                    initialValues.methods = processGenericsWithParams(classData.methods);
                    initialValues.enumValues = classData.enumValues;
                } catch (e) { console.error("Error parsing class data", e); }
            } else if (data.tipus === "method") {
                setMode("method");
                try {
                    const methodData = JSON.parse(decoded);
                    initialValues.methodName = methodData.methodName;
                    initialValues.modifiers = methodData.modifiers;
                    initialValues.returnType = methodData.returnType;
                    initialValues.parameters = processGenericsData(methodData.parameters);
                    initialValues.exceptionTypes = methodData.exceptionTypes;
                } catch (e) { console.error("Error parsing method data", e); }
            } else if (data.tipus === "content_class") {
                setMode("content_class");
                try {
                    const contentData = JSON.parse(decoded);
                    initialValues.refTestId = contentData.refTestId;
                    initialValues.searchText = contentData.searchText;
                    initialValues.mustContain = contentData.mustContain;
                } catch (e) { console.error("Error parsing content_class data", e); }
            } else if (data.tipus === "io_file") {
                setMode("io_file");
                try {
                    const ioData = JSON.parse(decoded);
                    let decodedInput = "";
                    if (ioData.input) {
                        try {
                            decodedInput = decodeURIComponent(escape(atob(ioData.input)));
                        } catch (e) {
                            try {
                                decodedInput = atob(ioData.input);
                            } catch (err) {
                                decodedInput = ioData.input;
                            }
                        }
                    }
                    initialValues.inputContent = decodedInput;
                    initialValues.outputContent = ioData.output;
                    initialValues.outputType = ioData.outputType;
                    initialValues.inputFileName = ioData.fileName || "datos.txt";
                } catch (e) { console.error("Error parsing io_file data", e); }
            } else if (data.tipus === "ui") {
                setMode("ui");
                try {
                    const uiData = JSON.parse(decoded);
                    initialValues.jspFile = uiData.jspFile || "index.jsp";
                    initialValues.requestParams = uiData.requestParams || "";
                    initialValues.uiValidationType = uiData.uiValidationType || "contains";
                    initialValues.expectedOutput = uiData.expectedOutput || "";
                } catch (e) { console.error("Error parsing ui data", e); }
            } else if (data.tipus === "exception_check") {
                setMode("exception_check");
                try {
                    const excData = JSON.parse(decoded);
                    initialValues.refTestId = excData.refTestId;
                    initialValues.methodName = excData.methodName;
                    initialValues.exceptionType = excData.exceptionType;
                    initialValues.expectedOutput = excData.expectedOutput;
                } catch (e) { console.error("Error parsing exception_check data", e); }
            } else if (data.tipus === "content") {
                setMode("content");
                // Try JSON first, fallback to raw string
                try {
                    const contentData = JSON.parse(decoded);
                    initialValues.searchText = contentData.text;
                    initialValues.mustContain = contentData.must;
                } catch (e) {
                    initialValues.searchText = decoded;
                    initialValues.mustContain = true;
                }
            } else if (data.tipus === "methodtest") {
                setMode("methodtest");
                // Format: JSON { refTestId, methodName }
                try {
                    const mData = JSON.parse(decoded);
                    initialValues.refTestId = mData.refTestId;
                    initialValues.methodName = mData.methodName;
                } catch (e) {
                    // Legacy fallback or empty
                }
                // E/S standard fields
                if (data.entrada) initialValues.entrada = data.entrada; // if stored in col
                else if (parts.length > 2) initialValues.entrada = atob(parts[2]); // if in fitxer

                initialValues.sortida = data.sortida || "";
            } else if (data.tipus === "db") {
                setMode("db");
                try {
                    const dbData = JSON.parse(decoded);
                    initialValues.sql_setup = dbData.sql_setup;
                    initialValues.sql_check = dbData.sql_check;
                    initialValues.expected_db_output = dbData.expected_db_output;
                } catch (e) { console.error("Error parsing db test data", e); }
            }

        } catch (e) {
            console.error("Error parsing test data", e);
        }
        form.setFieldsValue(initialValues);
    };

    // Helper to get readable label for class tests
    const getTestLabel = (t: any) => {
        try {
            const parts = t.fitxer ? t.fitxer.split(":") : [];
            const decoded = (parts.length > 2) ? atob(parts[2]) : (t.fitxer ? atob(t.fitxer) : "");
            const json = JSON.parse(decoded);
            return json.className ? `${json.className} (Test #${t.id})` : `Test #${t.id}`;
        } catch (e) {
            return `Test #${t.id}`;
        }
    };

    // Helper to parse a single 'method' test (Check Mètode) into its descriptor
    const parseMethodTest = (t: any) => {
        try {
            const parts = t.fitxer ? t.fitxer.split(":") : [];
            const decoded = (parts.length > 2) ? atob(parts[2]) : (t.fitxer ? atob(t.fitxer) : "");
            return JSON.parse(decoded);
        } catch (e) {
            return null;
        }
    };

    // Build dropdown options from the 'method' tests defined in this exercise.
    // Value is the method name (what the backend matches against), label shows the signature.
    const getSingleMethodOptions = () => {
        const seen = new Set<string>();
        const options: { value: string; label: string }[] = [];
        existingMethodTests.forEach(t => {
            const json = parseMethodTest(t);
            const name = json?.methodName;
            if (name && !seen.has(name)) {
                seen.add(name);
                const paramTypes = (json.parameters || []).map((p: any) => p.paramType).join(', ');
                options.push({ value: name, label: `${name}(${paramTypes})` });
            }
        });
        return options;
    };

    // Helper to get methods for selected class test
    const getMethodsForTest = (testId: string) => {
        const test = existingClassTests.find(t => t.id === testId);
        if (!test) return [];
        try {
            const parts = test.fitxer ? test.fitxer.split(":") : [];
            const decoded = (parts.length > 2) ? atob(parts[2]) : (test.fitxer ? atob(test.fitxer) : "");
            const json = JSON.parse(decoded);
            const methods = json.methods || [];
            // Map to options
            return methods.map((m: any) => ({
                value: m.methodName,
                label: `${m.methodName}(${m.parameters?.map((p: any) => p.paramType).join(',') || ''})`
            }));
        } catch (e) {
            return [];
        }
    }

    const handleCancel = () => {
        props.onClose(false);
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        const transformedValues = { ...values };

        // Transform custom parameters logic (reused)
        if (transformedValues.parameters) {
            transformedValues.parameters = transformedValues.parameters.map((p: any) => ({
                ...p,
                paramType: p.paramType === 'custom' ? p.customParamType : p.paramType,
                genericType: p.genericTypeKey && p.genericTypeValue ? `${p.genericTypeKey}, ${p.genericTypeValue}` : p.genericType
            }));
        }
        // ... (similar transforms for fields, etc., copied from EditTestModal)
        if (transformedValues.constructors) {
            transformedValues.constructors = transformedValues.constructors.map((c: any) => ({
                ...c,
                parameters: c.parameters ? c.parameters.map((p: any) => ({
                    ...p,
                    paramType: p.paramType === 'custom' ? p.customParamType : p.paramType,
                    genericType: p.genericTypeKey && p.genericTypeValue ? `${p.genericTypeKey}, ${p.genericTypeValue}` : p.genericType
                })) : []
            }));
        }
        if (transformedValues.fields) {
            transformedValues.fields = transformedValues.fields.map((f: any) => ({
                ...f,
                fieldType: f.fieldType === 'custom' ? f.customFieldType : f.fieldType,
                genericType: f.genericTypeKey && f.genericTypeValue ? `${f.genericTypeKey}, ${f.genericTypeValue}` : f.genericType
            }));
        }
        if (transformedValues.methods) {
            transformedValues.methods = transformedValues.methods.map((m: any) => ({
                ...m,
                returnType: m.returnType === 'custom' ? m.customReturnType : m.returnType,
                genericType: m.genericTypeKey && m.genericTypeValue ? `${m.genericTypeKey}, ${m.genericTypeValue}` : m.genericType,
                parameters: m.parameters ? m.parameters.map((p: any) => ({
                    ...p,
                    paramType: p.paramType === 'custom' ? p.customParamType : p.paramType,
                    genericType: p.genericTypeKey && p.genericTypeValue ? `${p.genericTypeKey}, ${p.genericTypeValue}` : p.genericType
                })) : []
            }));
        }

        try {
            // Note: PutJutgeProblemaIdTest handles formatting fitxer string?
            // "mode" is passed to Backend? Or "tipus"?
            // PostJutgeProblemaIdTest accepts body. "mode" determines "tipus".
            // We need to ensure logic sends correct "tipus" and "fitxer" (parsed) or separate fields.
            // The Dao seems to take `data`.
            // Let's assume Backend expects the raw values and constructs JSON, OR we construct JSON here.
            // Existing logic sent `...values, mode`.
            // We'll update the DAO or ensure Backend handles these new modes.
            // For complex types (class, method), EditTestModal sent the JSON in `fitxer`?
            // Awaiting check of PostJutgeProblemaIdTest...
            // It sends JSON body. Backend interprets.

            // Resolve refClassName for linked tests
            if (mode === 'content_class' || mode === 'exception_check') {
                const refId = transformedValues.refTestId;
                const refTest = existingClassTests.find(t => t.id === refId);
                if (refTest) {
                    try {
                        const parts = refTest.fitxer.split(":");
                        const decoded = atob(parts[2]);
                        const json = JSON.parse(decoded);
                        transformedValues.refClassName = json.className;
                    } catch (e) { console.error("Error parsing ref test", e); }
                }
            }

            if (mode === 'methodtest') {
                const refId = transformedValues.refTestId;
                const refTest = existingClassTests.find(t => t.id === refId);
                if (refTest) {
                    try {
                        const parts = refTest.fitxer.split(":");
                        const decoded = atob(parts[2]);
                        const json = JSON.parse(decoded);
                        transformedValues.refClassName = json.className;
                    } catch (e) { console.error("Error parsing ref test", e); }
                }
                const mData = {
                    refTestId: refId,
                    methodName: transformedValues.methodName,
                    className: transformedValues.refClassName
                };
                transformedValues.fitxer = JSON.stringify(mData);
            }

            if (mode === 'content') {
                const cData = {
                    text: transformedValues.searchText,
                    must: transformedValues.mustContain
                };
                transformedValues.fitxer = JSON.stringify(cData);
            }

            if (mode === 'method') {
                const methodData = {
                    methodName: transformedValues.methodName,
                    modifiers: transformedValues.modifiers || [],
                    returnType: transformedValues.returnType,
                    parameters: transformedValues.parameters || [],
                    exceptionTypes: transformedValues.exceptionTypes || []
                };
                transformedValues.fitxer = JSON.stringify(methodData);
            }

            if (mode === 'content_class') {
                const cData = {
                    refTestId: transformedValues.refTestId,
                    searchText: transformedValues.searchText,
                    mustContain: transformedValues.mustContain,
                    refClassName: transformedValues.refClassName
                };
                transformedValues.fitxer = JSON.stringify(cData);
            }

            if (mode === 'exception_check') {
                const exData = {
                    refTestId: transformedValues.refTestId,
                    methodName: transformedValues.methodName,
                    exceptionType: transformedValues.exceptionType,
                    expectedOutput: transformedValues.expectedOutput,
                    refClassName: transformedValues.refClassName,
                    entrada: transformedValues.entrada
                };
                transformedValues.fitxer = JSON.stringify(exData);
            }

            if (mode === 'io_file') {
                const rawInput = transformedValues.inputContent || "";
                let inputBase64 = "";
                try {
                    inputBase64 = btoa(unescape(encodeURIComponent(rawInput)));
                } catch (e) {
                    inputBase64 = btoa(rawInput);
                }

                const ioData = {
                    input: inputBase64,
                    output: transformedValues.outputContent,
                    outputType: transformedValues.outputType,
                    fileName: transformedValues.inputFileName || "datos.txt"
                };
                transformedValues.fitxer = JSON.stringify(ioData);
            }

            if (mode === 'ui') {
                const uiData = {
                    jspFile: transformedValues.jspFile || "index.jsp",
                    requestParams: transformedValues.requestParams || "",
                    uiValidationType: transformedValues.uiValidationType || "contains",
                    expectedOutput: transformedValues.expectedOutput || ""
                };
                transformedValues.fitxer = JSON.stringify(uiData);
            }

            if (mode === 'db') {
                const dbData = {
                    sql_setup: transformedValues.sql_setup,
                    sql_check: transformedValues.sql_check,
                    expected_db_output: transformedValues.expected_db_output
                };
                transformedValues.fitxer = JSON.stringify(dbData);
                transformedValues.tipus = "db";
            }

            const payload = { ...transformedValues, mode };

            // Edició d'un test agrupat E/S o Mètode + E/S: són dues files (entrada i sortida),
            // cal fer un PUT per cadascuna. El backend desa el text d'entrada si existeix, si no
            // el de sortida, així que enviem cada camp a la seva fila.
            if (props.testData && props.testData.__pair) {
                const entradaId = props.testData.entradaRow?.id;
                const sortidaId = props.testData.sortidaRow?.id;
                const methodName = mode === 'methodtest' ? transformedValues.methodName : undefined;

                if (entradaId != null) {
                    await PutJutgeProblemaIdTest(props.id_problema, entradaId, {
                        mode,
                        entrada: transformedValues.entrada || "",
                        sortida: "",
                        methodName
                    });
                }
                if (sortidaId != null) {
                    await PutJutgeProblemaIdTest(props.id_problema, sortidaId, {
                        mode,
                        entrada: "",
                        sortida: transformedValues.sortida || "",
                        methodName
                    });
                }
            } else if (props.testData && props.testData.id) {
                await PutJutgeProblemaIdTest(props.id_problema, props.testData.id, payload);
            } else {
                await PostJutgeProblemaIdTest(props.id_problema, payload);
            }
            props.onSave?.();
            props.onClose(false);
        } catch (err) {
            console.error("Error saving test:", err);
            message.error("Error guardant el test");
        } finally {
            setLoading(false);
        }
    };

    const addMainMethod = () => {
        const methods = form.getFieldValue("methods") || [];
        const mainMethod = {
            methodName: "main",
            modifiers: ["public", "static"],
            returnType: "void",
            parameters: [{ paramName: "args", paramType: "String[]" }]
        };
        form.setFieldsValue({ methods: [...methods, mainMethod] });
    };

    // Rellena el formulari del mode "method" (Check Mètode) amb la signatura del main:
    // public static void main(String[] args)
    const fillMainMethod = () => {
        form.setFieldsValue({
            methodName: "main",
            modifiers: ["public", "static"],
            returnType: "void",
            // String[] no és al llistat de tipus; fem servir "custom" (el patró ja suportat
            // pel formulari i transformat a onFinish via customParamType).
            parameters: [{ paramName: "args", paramType: "custom", customParamType: "String[]" }],
            exceptionTypes: []
        });
    };

    const addToString = () => {
        const methods = form.getFieldValue("methods") || [];
        const method = {
            methodName: "toString",
            modifiers: ["public"],
            returnType: "String",
            parameters: []
        };
        form.setFieldsValue({ methods: [...methods, method] });
    };

    const addEquals = () => {
        const methods = form.getFieldValue("methods") || [];
        const method = {
            methodName: "equals",
            modifiers: ["public"],
            returnType: "boolean",
            parameters: [{ paramName: "o", paramType: "Object" }]
        };
        form.setFieldsValue({ methods: [...methods, method] });
    };

    const addHashCode = () => {
        const methods = form.getFieldValue("methods") || [];
        const method = {
            methodName: "hashCode",
            modifiers: ["public"],
            returnType: "int",
            parameters: []
        };
        form.setFieldsValue({ methods: [...methods, method] });
    };

    const standardTypes = [
        { value: "byte", label: "byte" },
        { value: "short", label: "short" },
        { value: "int", label: "int" },
        { value: "long", label: "long" },
        { value: "float", label: "float" },
        { value: "double", label: "double" },
        { value: "boolean", label: "boolean" },
        { value: "char", label: "char" },
        { value: "String", label: "String" },
        { value: "Object", label: "Object" },
        { value: "void", label: "void" },
        { value: "custom", label: "Classe personalitzada" }
    ];

    const collectionTypes = [
        { value: "List", label: "List" },
        { value: "ArrayList", label: "ArrayList" },
        { value: "LinkedList", label: "LinkedList" },
        { value: "Set", label: "Set" },
        { value: "TreeSet", label: "TreeSet" },
        { value: "HashSet", label: "HashSet" },
        { value: "LinkedHashSet", label: "LinkedHashSet" },
        { value: "Map", label: "Map" },
        { value: "HashMap", label: "HashMap" },
        { value: "LinkedHashMap", label: "LinkedHashMap" },
        { value: "TreeMap", label: "TreeMap" },
        { value: "Queue", label: "Queue" },
        { value: "Stack", label: "Stack" },
    ];

    const allTypes = [...standardTypes, ...collectionTypes];

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={props.testData ? `Editar Test #${props.testData.id}` : "Nou Test"}
                open={props.visible}
                onCancel={handleCancel}
                footer={null}
                width={1000}
                maskClosable={false}
            >
                <Form.Item label="Tipus de test">
                    <Select
                        value={mode}
                        onChange={(val) => {
                            setMode(val);
                        }}
                        disabled={!!props.testData}
                        options={
                            !props.isMulti ? [
                                { value: "es", label: "Entrada i Sortida" },
                                { value: "content", label: "Contingut" },
                                { value: "method", label: "Check Mètode" },
                                { value: "methodtest", label: "Check Mètode + E/S" },
                            ] : [
                                { value: "es", label: "Entrada i Sortida" },
                                { value: "class", label: "Check Clase" },
                                { value: "db", label: "Base de Dades (Multi)" },
                                { value: "content_class", label: "Contingut en Clase" },
                                { value: "io_file", label: "Entrada per Fitxer" },
                                { value: "exception_check", label: "Detecció d'Excepcions" },
                                { value: "ui", label: "Interfície d'Usuari (JSP)" },
                            ]
                        }
                    />
                </Form.Item>

                <Form form={form} onFinish={onFinish} layout="vertical">

                    {/* --- ES MODE --- */}
                    {mode === "es" && (
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Entrada" name="entrada">
                                    <TextArea rows={10} disabled={senseEntrada} />
                                </Form.Item>
                                <Checkbox checked={senseEntrada} onChange={e => {
                                    setSenseEntrada(e.target.checked);
                                    if (e.target.checked) form.setFieldsValue({ entrada: "" });
                                }}>Sense entrada</Checkbox>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Sortida Esperada" name="sortida" rules={[{ required: true }]}>
                                    <TextArea rows={10} />
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* --- CLASS MODE --- */}
                    {mode === "class" && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Nom de la Clase" name="className" rules={[{ required: true }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Tipus" name="classType" initialValue="class">
                                        <Select options={[
                                            { value: "class", label: "class" },
                                            { value: "interface", label: "interface" },
                                            { value: "abstract class", label: "abstract class" },
                                            { value: "enum", label: "enum" },
                                        ]} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Modificadors" name="classModifiers" initialValue={["public"]}>
                                <Select mode="multiple" options={[
                                    { value: "public", label: "public" }, { value: "abstract", label: "abstract" }, { value: "final", label: "final" }
                                ]} />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Extends (Clase Pare)" name="extendedClass">
                                        <Input placeholder="Nom de la clase pare (opcional)" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Implements (Interfícies)" name="implementedInterfaces">
                                        <Select mode="tags" placeholder="Interfícies implementades" tokenSeparators={[',', ' ']} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Enum Values Section */}
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, curr) => prev.classType !== curr.classType}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('classType') === 'enum' ? (
                                        <>
                                            <h5>Valors de l'Enum</h5>
                                            <Form.List name="enumValues">
                                                {(fields, { add, remove }) => (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                        {fields.map(field => (
                                                            <Space key={field.key} align="baseline">
                                                                <Form.Item {...field} rules={[{ required: true, message: 'Valor requerit' }]}>
                                                                    <Input placeholder="VALOR" />
                                                                </Form.Item>
                                                                <DeleteOutlined onClick={() => remove(field.name)} style={{ color: 'red' }} />
                                                            </Space>
                                                        ))}
                                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>Afegir Valor</Button>
                                                    </div>
                                                )}
                                            </Form.List>
                                            <Divider />
                                        </>
                                    ) : null
                                }
                            </Form.Item>

                            {/* ... Constructors ... */}
                            <h5>Constructors</h5>
                            <Form.List name="constructors">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(field => (
                                            <div key={field.key} style={{ border: '1px solid #ddd', padding: 5, marginBottom: 5 }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <Button danger size="small" onClick={() => remove(field.name)} icon={<DeleteOutlined />} />
                                                </div>
                                                <Row gutter={8}>
                                                    <Col span={24}>
                                                        <Form.Item {...field} name={[field.name, "modifiers"]} label="Modificadors">
                                                            <Select mode="multiple" options={[
                                                                { value: "public", label: "public" },
                                                                { value: "private", label: "private" },
                                                                { value: "protected", label: "protected" }
                                                            ]} size="small" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <h6>Paràmetres</h6>
                                                <Form.List name={[field.name, "parameters"]}>
                                                    {(params, { add: addParam, remove: removeParam }) => (
                                                        <>
                                                            {params.map(p => (
                                                                <Space key={p.key} style={{ display: 'flex', marginBottom: 2 }} align="baseline">
                                                                    <Form.Item {...p} name={[p.name, "paramType"]} rules={[{ required: true }]}>
                                                                        <Select options={allTypes} placeholder="Type" size="small" style={{ width: 100 }} />
                                                                    </Form.Item>
                                                                    <Form.Item {...p} name={[p.name, "paramName"]} rules={[{ required: true }]}>
                                                                        <Input placeholder="Name" size="small" />
                                                                    </Form.Item>
                                                                    <Form.Item
                                                                        noStyle
                                                                        shouldUpdate={(prev, curr) => {
                                                                            // Access nested value is tricky in shouldUpdate with lists.
                                                                            // Using dependencies in a wrapped item might be cleaner but let's try direct render if possible.
                                                                            // Actually, simpler to just always render the item and rely on deps.
                                                                            return true;
                                                                        }}
                                                                    >
                                                                        {({ getFieldValue }) => {
                                                                            const type = getFieldValue(['constructors', field.name, 'parameters', p.name, 'paramType']);
                                                                            if (type === 'custom') {
                                                                                return (
                                                                                    <Form.Item {...p} name={[p.name, "customParamType"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                        <Input placeholder="Custom Class" size="small" />
                                                                                    </Form.Item>
                                                                                );
                                                                            }
                                                                            if (mapTypes.includes(type)) {
                                                                                return (
                                                                                    <Space size="small">
                                                                                        <Form.Item {...p} name={[p.name, "genericTypeKey"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                            <Input placeholder="Key Type" size="small" />
                                                                                        </Form.Item>
                                                                                        <Form.Item {...p} name={[p.name, "genericTypeValue"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                            <Input placeholder="Value Type" size="small" />
                                                                                        </Form.Item>
                                                                                    </Space>
                                                                                );
                                                                            }
                                                                            if (collectionTypes.some(ct => ct.value === type)) {
                                                                                return (
                                                                                    <Form.Item {...p} name={[p.name, "genericType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                                        <Input placeholder="Tipus genèric" size="small" />
                                                                                    </Form.Item>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        }}
                                                                    </Form.Item>
                                                                    <DeleteOutlined onClick={() => removeParam(p.name)} style={{ color: 'red' }} />
                                                                </Space>
                                                            ))}
                                                            <Button type="dashed" size="small" onClick={() => addParam()} icon={<PlusOutlined />}>Param</Button>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </div>
                                        ))}
                                        <Button type="dashed" size="small" onClick={() => add({ modifiers: ["public"] })} icon={<PlusOutlined />}>Afegir Constructor</Button>
                                    </>
                                )}
                            </Form.List>
                            <Divider />

                            {/* ... Fields ... */}
                            <h5>Atributs (Fields)</h5>
                            <Form.List name="fields">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(field => (
                                            <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="start">
                                                <Form.Item {...field} name={[field.name, "modifiers"]} style={{ width: 150 }}>
                                                    <Select mode="multiple" placeholder="Modificadors" options={[
                                                        { value: "private", label: "private" },
                                                        { value: "public", label: "public" },
                                                        { value: "protected", label: "protected" },
                                                        { value: "static", label: "static" },
                                                        { value: "final", label: "final" },
                                                    ]} size="small" />
                                                </Form.Item>
                                                <Form.Item {...field} name={[field.name, "fieldType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                    <Select options={allTypes} placeholder="Tipus" size="small" />
                                                </Form.Item>
                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prev, curr) => true}
                                                >
                                                    {({ getFieldValue }) => {
                                                        const type = getFieldValue(['fields', field.name, 'fieldType']);
                                                        if (type === 'custom') {
                                                            return (
                                                                <Form.Item {...field} name={[field.name, "customFieldType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                    <Input placeholder="Custom Class" size="small" />
                                                                </Form.Item>
                                                            );
                                                        }
                                                        if (mapTypes.includes(type)) {
                                                            return (
                                                                <Space size="small">
                                                                    <Form.Item {...field} name={[field.name, "genericTypeKey"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                        <Input placeholder="Key Type" size="small" />
                                                                    </Form.Item>
                                                                    <Form.Item {...field} name={[field.name, "genericTypeValue"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                        <Input placeholder="Value Type" size="small" />
                                                                    </Form.Item>
                                                                </Space>
                                                            );
                                                        }
                                                        if (collectionTypes.some(ct => ct.value === type)) {
                                                            return (
                                                                <Form.Item {...field} name={[field.name, "genericType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                    <Input placeholder="Tipus genèric" size="small" />
                                                                </Form.Item>
                                                            );
                                                        }
                                                        return null;
                                                    }}
                                                </Form.Item>
                                                <Form.Item {...field} name={[field.name, "fieldName"]} rules={[{ required: true }]}>
                                                    <Input placeholder="Nom" size="small" />
                                                </Form.Item>
                                                <Form.Item {...field} name={[field.name, "checkGetter"]} valuePropName="checked">
                                                    <Checkbox>Getter</Checkbox>
                                                </Form.Item>
                                                <Form.Item {...field} name={[field.name, "checkSetter"]} valuePropName="checked">
                                                    <Checkbox>Setter</Checkbox>
                                                </Form.Item>
                                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                            </Space>
                                        ))}
                                        <Button type="dashed" size="small" onClick={() => add({ modifiers: ["private"] })} icon={<PlusOutlined />}>Afegir Atribut</Button>
                                    </>
                                )}
                            </Form.List>
                            <Divider />

                            {/* ... Methods ... */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                <h5>Mètodes</h5>
                                <Space>
                                    <Button size="small" onClick={addMainMethod}>+ 'main'</Button>
                                    <Button size="small" onClick={addToString}>+ 'toString'</Button>
                                    <Button size="small" onClick={addEquals}>+ 'equals'</Button>
                                    <Button size="small" onClick={addHashCode}>+ 'hashCode'</Button>
                                </Space>
                            </div>
                            <Form.List name="methods">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(field => (
                                            <div key={field.key} style={{ border: '1px solid #ddd', padding: 5, marginBottom: 5 }}>
                                                <Row gutter={8}>
                                                    <Col span={8}>
                                                        <Form.Item {...field} name={[field.name, "methodName"]} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                                            <Input placeholder="Method Name" size="small" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Form.Item {...field} name={[field.name, "returnType"]} style={{ marginBottom: 0 }}>
                                                            <Select options={allTypes} placeholder="Return Type" size="small" />
                                                        </Form.Item>
                                                        <Form.Item
                                                            noStyle
                                                            shouldUpdate={(prev, curr) => true}
                                                        >
                                                            {({ getFieldValue }) => {
                                                                const type = getFieldValue(['methods', field.name, 'returnType']);
                                                                if (type === 'custom') {
                                                                    return (
                                                                        <Form.Item {...field} name={[field.name, "customReturnType"]} rules={[{ required: true }]} style={{ marginTop: 5 }}>
                                                                            <Input placeholder="Custom Return Type" size="small" />
                                                                        </Form.Item>
                                                                    );
                                                                }
                                                                if (mapTypes.includes(type)) {
                                                                    return (
                                                                        <Space size="small" style={{ marginTop: 5 }}>
                                                                            <Form.Item {...field} name={[field.name, "genericTypeKey"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                <Input placeholder="Key Type" size="small" />
                                                                            </Form.Item>
                                                                            <Form.Item {...field} name={[field.name, "genericTypeValue"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                <Input placeholder="Value Type" size="small" />
                                                                            </Form.Item>
                                                                        </Space>
                                                                    );
                                                                }
                                                                if (collectionTypes.some(ct => ct.value === type)) {
                                                                    return (
                                                                        <Form.Item {...field} name={[field.name, "genericType"]} rules={[{ required: true }]} style={{ marginTop: 5 }}>
                                                                            <Input placeholder="Tipus genèric" size="small" />
                                                                        </Form.Item>
                                                                    );
                                                                }
                                                                return null;
                                                            }}
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={8} style={{ textAlign: 'right' }}>
                                                        <Button danger size="small" onClick={() => remove(field.name)} icon={<DeleteOutlined />} />
                                                    </Col>
                                                </Row>
                                                {/* Modifiers & Exceptions */}
                                                <Row gutter={8} style={{ marginTop: 5 }}>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, "modifiers"]} style={{ marginBottom: 0 }}>
                                                            <Select mode="multiple" placeholder="Modifiers" options={[
                                                                { value: "public", label: "public" },
                                                                { value: "static", label: "static" },
                                                                { value: "abstract", label: "abstract" },
                                                                { value: "default", label: "default" },
                                                            ]} size="small" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Form.Item {...field} name={[field.name, "exceptionTypes"]} style={{ marginBottom: 0 }}>
                                                            <Select mode="tags" placeholder="Exceptions Thrown" size="small" />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>

                                                <h6>Paràmetres</h6>
                                                <Form.List name={[field.name, "parameters"]}>
                                                    {(params, { add: addParam, remove: removeParam }) => (
                                                        <>
                                                            {params.map(p => (
                                                                <Space key={p.key} style={{ display: 'flex', marginBottom: 2 }} align="baseline">
                                                                    <Form.Item {...p} name={[p.name, "paramType"]} rules={[{ required: true }]}>
                                                                        <Select options={allTypes} placeholder="Type" size="small" style={{ width: 100 }} />
                                                                    </Form.Item>
                                                                    <Form.Item {...p} name={[p.name, "paramName"]} rules={[{ required: true }]}>
                                                                        <Input placeholder="Name" size="small" />
                                                                    </Form.Item>
                                                                    <Form.Item
                                                                        noStyle
                                                                        shouldUpdate={(prev, curr) => true}
                                                                    >
                                                                        {({ getFieldValue }) => {
                                                                            const type = getFieldValue(['methods', field.name, 'parameters', p.name, 'paramType']);
                                                                            if (type === 'custom') {
                                                                                return (
                                                                                    <Form.Item {...p} name={[p.name, "customParamType"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                        <Input placeholder="Custom Class" size="small" />
                                                                                    </Form.Item>
                                                                                );
                                                                            }
                                                                            if (mapTypes.includes(type)) {
                                                                                return (
                                                                                    <Space size="small">
                                                                                        <Form.Item {...p} name={[p.name, "genericTypeKey"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                            <Input placeholder="Key Type" size="small" />
                                                                                        </Form.Item>
                                                                                        <Form.Item {...p} name={[p.name, "genericTypeValue"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                                            <Input placeholder="Value Type" size="small" />
                                                                                        </Form.Item>
                                                                                    </Space>
                                                                                );
                                                                            }
                                                                            if (collectionTypes.some(ct => ct.value === type)) {
                                                                                return (
                                                                                    <Form.Item {...p} name={[p.name, "genericType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                                        <Input placeholder="Tipus" size="small" />
                                                                                    </Form.Item>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        }}
                                                                    </Form.Item>
                                                                    <DeleteOutlined onClick={() => removeParam(p.name)} style={{ color: 'red' }} />
                                                                </Space>
                                                            ))}
                                                            <Button type="dashed" size="small" onClick={() => addParam()} icon={<PlusOutlined />}>Param</Button>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </div>
                                        ))}
                                        <Button type="dashed" size="small" onClick={() => add()} block icon={<PlusOutlined />}>Afegir Mètode</Button>
                                    </>
                                )}
                            </Form.List>
                        </>
                    )}

                    {/* --- CONTENT MODE (Legacy/Enhanced) --- */}
                    {
                        mode === "content" && (
                            <>
                                <Form.Item label="Text que ha de contenir (o no)" name="searchText" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="mustContain" valuePropName="checked" initialValue={true}>
                                    <Checkbox>Ha de contenir el text? (Desmarcar per 'No ha de contenir')</Checkbox>
                                </Form.Item>
                            </>
                        )
                    }

                    {/* --- METHOD TEST (Execució) --- */}
                    {
                        mode === "methodtest" && (
                            <>
                                {/* En exercicis multi-fitxer cal indicar la classe on viu el mètode.
                                    En exercicis d'un sol fitxer no hi ha classes a seleccionar. */}
                                {props.isMulti && (
                                    <Form.Item label="Selecciona Test de Clase" name="refTestId" rules={[{ required: true }]}>
                                        <Select placeholder="Selecciona la clase on es troba el mètode">
                                            {existingClassTests.map(t => (
                                                <Select.Option key={t.id} value={t.id}>{getTestLabel(t)}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )}

                                {/* Nom del mètode: en un sol fitxer, desplegable amb els tests de tipus
                                    "Check Mètode" definits al mateix exercici. Si no n'hi ha cap, input lliure. */}
                                {!props.isMulti && getSingleMethodOptions().length > 0 ? (
                                    <Form.Item label="Nom del Mètode" name="methodName" rules={[{ required: true }]}>
                                        <Select
                                            placeholder="Selecciona el mètode a executar"
                                            options={getSingleMethodOptions()}
                                        />
                                    </Form.Item>
                                ) : (
                                    <Form.Item label="Nom del Mètode" name="methodName" rules={[{ required: true }]}>
                                        <Input placeholder="Nom del mètode a executar" />
                                    </Form.Item>
                                )}

                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="Entrada (Arguments)" name="entrada">
                                            <TextArea rows={5} placeholder="Arguments separats per espais o segons format" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="Sortida Esperada (Return)" name="sortida" rules={[{ required: true }]}>
                                            <TextArea rows={5} placeholder="Resultat esperat" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </>
                        )
                    }

                    {/* --- METHOD MODE --- */}
                    {
                        mode === "method" && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                    <Button size="small" onClick={fillMainMethod}>+ main (public static void)</Button>
                                </div>
                                <Form.Item label="Nom" name="methodName" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label="Modificadors" name="modifiers">
                                    <Select mode="multiple" options={[{ value: "public", label: "public" }, { value: "static", label: "static" }]} />
                                </Form.Item>
                                <Form.Item label="Return Type" name="returnType" rules={[{ required: true }]}>
                                    <Select options={allTypes} />
                                </Form.Item>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, curr) => prev.returnType !== curr.returnType}
                                >
                                    {({ getFieldValue }) =>
                                        getFieldValue('returnType') === 'custom' ? (
                                            <Form.Item label="Tipus Personalitzat (Return)" name="customReturnType" rules={[{ required: true }]}>
                                                <Input placeholder="Nom de la clase" />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                                <Form.Item label="Excepcions" name="exceptionTypes">
                                    <Select mode="tags" placeholder="Excepcions que llança" />
                                </Form.Item>

                                <h6>Paràmetres</h6>
                                <Form.List name="parameters">
                                    {(params, { add: addParam, remove: removeParam }) => (
                                        <>
                                            {params.map(p => (
                                                <Space key={p.key} style={{ display: 'flex', marginBottom: 2 }} align="baseline">
                                                    <Form.Item {...p} name={[p.name, "paramType"]} rules={[{ required: true }]}>
                                                        <Select options={allTypes} placeholder="Type" size="small" style={{ width: 120 }} />
                                                    </Form.Item>
                                                    <Form.Item {...p} name={[p.name, "paramName"]} rules={[{ required: true }]}>
                                                        <Input placeholder="Name" size="small" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        noStyle
                                                        shouldUpdate={() => true}
                                                    >
                                                        {({ getFieldValue }) => {
                                                            const type = getFieldValue(['parameters', p.name, 'paramType']);
                                                            if (type === 'custom') {
                                                                return (
                                                                    <Form.Item {...p} name={[p.name, "customParamType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                        <Input placeholder="Custom Class" size="small" />
                                                                    </Form.Item>
                                                                );
                                                            }
                                                            if (mapTypes.includes(type)) {
                                                                return (
                                                                    <Space size="small">
                                                                        <Form.Item {...p} name={[p.name, "genericTypeKey"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                            <Input placeholder="Key Type" size="small" />
                                                                        </Form.Item>
                                                                        <Form.Item {...p} name={[p.name, "genericTypeValue"]} rules={[{ required: true }]} style={{ width: 100 }}>
                                                                            <Input placeholder="Value Type" size="small" />
                                                                        </Form.Item>
                                                                    </Space>
                                                                );
                                                            }
                                                            if (collectionTypes.some(ct => ct.value === type)) {
                                                                return (
                                                                    <Form.Item {...p} name={[p.name, "genericType"]} rules={[{ required: true }]} style={{ width: 120 }}>
                                                                        <Input placeholder="Tipus" size="small" />
                                                                    </Form.Item>
                                                                );
                                                            }
                                                            return null;
                                                        }}
                                                    </Form.Item>
                                                    <DeleteOutlined onClick={() => removeParam(p.name)} style={{ color: 'red' }} />
                                                </Space>
                                            ))}
                                            <Button type="dashed" size="small" onClick={() => addParam()} icon={<PlusOutlined />}>Afegir Paràmetre</Button>
                                        </>
                                    )}
                                </Form.List>
                            </>
                        )
                    }

                    {/* --- CONTENT CLASS MODE --- */}
                    {
                        mode === "content_class" && (
                            <>
                                <Form.Item label="Selecciona Test de Clase" name="refTestId" rules={[{ required: true }]}>
                                    <Select placeholder="Selecciona un test de clase existent">
                                        {existingClassTests.map(t => (
                                            <Select.Option key={t.id} value={t.id}>{getTestLabel(t)}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Text a buscar" name="searchText" rules={[{ required: true }]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item name="mustContain" valuePropName="checked">
                                    <Checkbox>Ha de contenir el text? (Desmarcar per 'No ha de contenir')</Checkbox>
                                </Form.Item>
                            </>
                        )
                    }

                    {/* --- IO FILE MODE --- */}
                    {
                        mode === "io_file" && (
                            <>
                                <Form.Item label="Nom del fitxer (Ex: datos.txt)" name="inputFileName" rules={[{ required: true }]}>
                                    <Input placeholder="datos.txt" />
                                </Form.Item>
                                <Form.Item label="Contingut del Fitxer d'Entrada" name="inputContent">
                                    <TextArea rows={5} placeholder="Introdueix el contingut del fitxer d'entrada" />
                                </Form.Item>
                                <Form.Item label="Tipus de Sortida" name="outputType" initialValue="text">
                                    <Select options={[{ value: "text", label: "Text" }, { value: "file", label: "Fitxer de Sortida" }]} />
                                </Form.Item>
                                <Form.Item label="Sortida Esperada" name="outputContent">
                                    <TextArea rows={5} placeholder="Contingut esperat" />
                                </Form.Item>
                            </>
                        )
                    }

                    {/* --- UI (JSP) MODE --- */}
                    {
                        mode === "ui" && (
                            <>
                                <Form.Item label="Nom del fitxer JSP (Ex: index.jsp)" name="jspFile" rules={[{ required: true }]}>
                                    <Input placeholder="index.jsp" />
                                </Form.Item>
                                <Form.Item label="Paràmetres de la Request (Query String, Ex: a=1&b=2)" name="requestParams">
                                    <Input placeholder="a=1&b=2" />
                                </Form.Item>
                                <Form.Item label="Tipus de Validació" name="uiValidationType" initialValue="contains">
                                    <Select options={[
                                        { value: "contains", label: "La sortida conté el text" },
                                        { value: "exact", label: "La sortida és idèntica" }
                                    ]} />
                                </Form.Item>
                                <Form.Item label="Sortida HTML Esperada" name="expectedOutput" rules={[{ required: true }]}>
                                    <TextArea rows={8} placeholder="HTML o text esperat" />
                                </Form.Item>
                            </>
                        )
                    }

                    {/* --- EXCEPTION CHECK MODE --- */}
                    {
                        mode === "exception_check" && (
                            <>
                                <Form.Item label="Selecciona Test de Clase" name="refTestId" rules={[{ required: true }]}>
                                    <Select placeholder="Selecciona un test de clase">
                                        {existingClassTests.map(t => (
                                            <Select.Option key={t.id} value={t.id}>{getTestLabel(t)}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, curr) => prev.refTestId !== curr.refTestId}
                                >
                                    {({ getFieldValue }) => {
                                        const refId = getFieldValue('refTestId');
                                        const methodOptions = refId ? getMethodsForTest(refId) : [];
                                        return (
                                            <Form.Item label="Mètode a provar" name="methodName" rules={[{ required: true }]}>
                                                <Select placeholder="Selecciona un mètode" options={methodOptions} />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                                <Form.Item label="Entrada (Paràmetres)" name="entrada" rules={[{ required: true }]}>
                                    <TextArea rows={4} placeholder="Paràmetres d'entrada" />
                                </Form.Item>
                                <Form.Item label="Excepció Esperada" name="exceptionType" rules={[{ required: true }]}>
                                    <Input placeholder="java.lang.IllegalArgumentException" />
                                </Form.Item>
                                <Form.Item label="Missatge de Sortida Esperat" name="expectedOutput">
                                    <TextArea rows={3} />
                                </Form.Item>
                            </>
                        )
                    }

                    {/* --- DB MODE --- */}
                    {mode === "db" && (
                        <>
                            <Divider>Configuració Base de Dades</Divider>
                            <Typography.Text type="secondary">
                                El setup s'executa ABANS del codi de l'alumne. El check s'executa DESPRÉS.
                            </Typography.Text>
                            <Row gutter={16} style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <Form.Item label="SQL Setup (DDL/DML Inicial)" name="sql_setup" rules={[{ required: true }]}>
                                        <Editor
                                            height="200px"
                                            defaultLanguage="sql"
                                            theme="light"
                                            options={{ minimap: { enabled: false }, scrollBeyondLastLine: false }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item label="SQL Check (Queries de validació)" name="sql_check" rules={[{ required: true }]}>
                                        <Editor
                                            height="200px"
                                            defaultLanguage="sql"
                                            theme="light"
                                            options={{ minimap: { enabled: false }, scrollBeyondLastLine: false }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Resultat Esperat de la Query de Check" name="expected_db_output" rules={[{ required: true }]}>
                                <TextArea rows={4} placeholder="Resultat segons format de validació del backend" />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item style={{ marginTop: 20 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={enviado} style={{ backgroundColor: JutgeColors.colors.color4 }}>
                                Guardar
                            </Button>
                            <Button onClick={handleCancel}>Cancel·lar</Button>
                        </Space>
                    </Form.Item>
                </Form >
            </Modal >
        </ConfigProvider >
    );
};

export default TestFormModal;
